import { z } from "zod";

/**
 * Shared DTO cho Referral — invite & track (US-E12.F4-0078 · @UC0016/0017/0018).
 *
 * Một nguồn sự thật shape api ↔ mobile (DRY):
 *  - GET/POST /v1/me/referrals → code duy nhất của tôi (idempotent) + link share + danh sách
 *    người tôi đã mời (chỉ tên + status) + đếm converted/pending.
 *  - Attribution (US-2) chạy server-side lúc đăng ký (register kèm referralCode) — KHÔNG có
 *    endpoint riêng trong slice này (theo story: hook vào auth register).
 *
 * BẢO MẬT (invariants):
 *  - PII: response track CHỈ trả `name` + `status` của người được mời — KHÔNG email/phone
 *    (guardrail C2/C3, giống pattern JSON-scan 0022/0045).
 *  - IDOR: referrerUserId lấy từ token (không nhận từ body/query).
 *  - Code CSPRNG (không đoán được) — sinh server-side; client chỉ đọc/chia sẻ.
 */

/** Độ dài code referral (base32 Crockford, uppercase, 8 ký tự). */
export const REFERRAL_CODE_LENGTH = 8;

/**
 * Regex code hợp lệ dùng để validate biên khi nhận referralCode lúc đăng ký (C5).
 * Base32 Crockford: 0-9 + A-Z trừ I, L, O, U (giảm nhầm lẫn khi gõ tay).
 */
export const REFERRAL_CODE_REGEX = /^[0-9A-HJKMNP-TV-Z]{8}$/;

/** Status của một referral: pending (chưa convert) | converted (đã đăng ký thành công). */
export const referralStatusSchema = z.enum(["pending", "converted"]);
export type ReferralStatus = z.infer<typeof referralStatusSchema>;

/** Một dòng người-tôi-mời trong danh sách track. CHỈ tên + status (KHÔNG PII email/phone). */
export const referralInviteeSchema = z
  .object({
    /** Tên hiển thị của người được mời (null nếu chưa đặt). KHÔNG email/phone (C2/C3). */
    name: z.string().nullable(),
    status: referralStatusSchema,
  })
  .strict();
export type ReferralInvitee = z.infer<typeof referralInviteeSchema>;

/** Đếm gộp cho UI track. */
export const referralCountsSchema = z
  .object({
    total: z.number().int().nonnegative(),
    converted: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
  })
  .strict();
export type ReferralCounts = z.infer<typeof referralCountsSchema>;

/**
 * Response GET|POST /v1/me/referrals — code của tôi + link share + track.
 * `shareLink` chứa code để chia sẻ (deep-link app, US-1 AC2).
 */
export const myReferralResponseSchema = z
  .object({
    code: z.string().regex(REFERRAL_CODE_REGEX),
    shareLink: z.string().min(1),
    invited: z.array(referralInviteeSchema),
    counts: referralCountsSchema,
  })
  .strict();
export type MyReferralResponse = z.infer<typeof myReferralResponseSchema>;
